/* eslint-disable i18next/no-literal-string -- MIME types and file extensions are wire values the browser and assets-api match byte for byte, and the type names are format names that read the same in every locale. */
export const BYTES_PER_MEGABYTE = 1024 * 1024;

/** The assets-api upload endpoint refuses anything larger, so a section cannot allow more. */
export const MAX_FILE_SIZE_MB = 100;

export interface FileTypeEntry {
  mimeType: string;
  extensions: readonly string[];
  /** How a briefer recognises the format. */
  name: string;
}

export const FILE_TYPE_GROUPS = [
  {
    id: 'pdf',
    labelKey: 'knowledgeHub.briefSectionConfigFields.fileTypeGroups.pdf',
    types: [{ mimeType: 'application/pdf', extensions: ['.pdf'], name: 'PDF' }],
  },
  {
    id: 'images',
    labelKey: 'knowledgeHub.briefSectionConfigFields.fileTypeGroups.images',
    types: [
      { mimeType: 'image/jpeg', extensions: ['.jpg', '.jpeg'], name: 'JPG' },
      { mimeType: 'image/png', extensions: ['.png'], name: 'PNG' },
      { mimeType: 'image/gif', extensions: ['.gif'], name: 'GIF' },
      { mimeType: 'image/webp', extensions: ['.webp'], name: 'WebP' },
    ],
  },
  {
    id: 'word',
    labelKey: 'knowledgeHub.briefSectionConfigFields.fileTypeGroups.word',
    types: [
      { mimeType: 'application/msword', extensions: ['.doc'], name: 'DOC' },
      {
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        extensions: ['.docx'],
        name: 'DOCX',
      },
    ],
  },
  {
    id: 'excel',
    labelKey: 'knowledgeHub.briefSectionConfigFields.fileTypeGroups.excel',
    types: [
      { mimeType: 'application/vnd.ms-excel', extensions: ['.xls'], name: 'XLS' },
      {
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        extensions: ['.xlsx'],
        name: 'XLSX',
      },
      { mimeType: 'text/csv', extensions: ['.csv'], name: 'CSV' },
    ],
  },
  {
    id: 'powerpoint',
    labelKey: 'knowledgeHub.briefSectionConfigFields.fileTypeGroups.powerpoint',
    types: [
      { mimeType: 'application/vnd.ms-powerpoint', extensions: ['.ppt'], name: 'PPT' },
      {
        mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        extensions: ['.pptx'],
        name: 'PPTX',
      },
    ],
  },
  {
    id: 'video',
    labelKey: 'knowledgeHub.briefSectionConfigFields.fileTypeGroups.video',
    types: [
      { mimeType: 'video/mp4', extensions: ['.mp4'], name: 'MP4' },
      { mimeType: 'video/quicktime', extensions: ['.mov'], name: 'MOV' },
    ],
  },
] as const satisfies readonly {
  id: string;
  labelKey: string;
  types: readonly FileTypeEntry[];
}[];
/* eslint-enable i18next/no-literal-string */

export type FileTypeGroup = (typeof FILE_TYPE_GROUPS)[number];
export type FileTypeGroupId = FileTypeGroup['id'];

const TYPE_BY_MIME = new Map<string, FileTypeEntry>(
  FILE_TYPE_GROUPS.flatMap((group) => group.types.map((type) => [type.mimeType, type] as const)),
);

const MIME_BY_EXTENSION = new Map<string, string>(
  [...TYPE_BY_MIME.values()].flatMap((type) =>
    type.extensions.map((extension) => [extension, type.mimeType] as const),
  ),
);

/** Agrees with `fileAcceptAttribute`, so a file the picker admits by extension is not refused here. */
export function isAcceptedFile(
  file: Pick<File, 'name' | 'type'>,
  acceptedMimeTypes: readonly string[],
): boolean {
  if (acceptedMimeTypes.includes(file.type)) return true;
  const dot = file.name.lastIndexOf('.');
  if (dot === -1) return false;
  const mimeType = MIME_BY_EXTENSION.get(file.name.slice(dot).toLowerCase());
  return mimeType !== undefined && acceptedMimeTypes.includes(mimeType);
}

function groupMimeTypes(groupId: FileTypeGroupId): string[] {
  const group = FILE_TYPE_GROUPS.find((candidate) => candidate.id === groupId);
  return group ? group.types.map((type) => type.mimeType) : [];
}

export function selectedFileTypeGroupIds(acceptedMimeTypes: readonly string[]): FileTypeGroupId[] {
  return FILE_TYPE_GROUPS.filter((group) =>
    group.types.every((type) => acceptedMimeTypes.includes(type.mimeType)),
  ).map((group) => group.id);
}

/** MIME types outside every group (configs saved before the catalog existed) are kept as they are. */
export function toggleFileTypeGroup(
  acceptedMimeTypes: readonly string[],
  groupId: FileTypeGroupId,
  selected: boolean,
): string[] {
  const groupTypes = groupMimeTypes(groupId);
  if (!selected) {
    return acceptedMimeTypes.filter((mimeType) => !groupTypes.includes(mimeType));
  }
  const missing = groupTypes.filter((mimeType) => !acceptedMimeTypes.includes(mimeType));
  return [...acceptedMimeTypes, ...missing];
}

/**
 * The extensions ride along with each MIME type because some systems report no MIME type, or a
 * different one, for a file the author meant to allow (a `.csv` from Excel on Windows).
 */
export function fileAcceptAttribute(acceptedMimeTypes: readonly string[]): string {
  return acceptedMimeTypes
    .flatMap((mimeType) => [mimeType, ...(TYPE_BY_MIME.get(mimeType)?.extensions ?? [])])
    .join(',');
}

export function fileTypeDisplayNames(acceptedMimeTypes: readonly string[]): string[] {
  const names = acceptedMimeTypes.map(
    (mimeType) =>
      TYPE_BY_MIME.get(mimeType)?.name ?? (mimeType.split('/')[1] ?? mimeType).toUpperCase(),
  );
  return [...new Set(names)];
}
